import { useState, useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapSpot } from '@/types/spots';
import { motion, AnimatePresence } from 'framer-motion';
import { Wifi, WifiOff, Plug, Volume2, Coffee, Hotel, BookOpen, Laptop, Sparkles, Dumbbell, UtensilsCrossed } from 'lucide-react';
import { getCurrentNoise } from '@/utils/noiseMatrix';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiemFpbmg0MiIsImEiOiJjbTR1NHBoYjMwZXQ4MmtzYWIwZzJzaWR6In0.gE5QluhHA_G_TzvGpOsRdg';
const API_BASE = 'https://spotsvc.up.railway.app';

const CATEGORY_EMOJIS: Record<string, string> = {
  cafe: '☕', gym: '🏋️', hotel_lobby: '🏨', coworking: '💻',
  library: '📚', restaurant: '🍽️', other: '✨',
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  cafe: <Coffee className="w-5 h-5" />,
  hotel_lobby: <Hotel className="w-5 h-5" />,
  library: <BookOpen className="w-5 h-5" />,
  coworking: <Laptop className="w-5 h-5" />,
  gym: <Dumbbell className="w-5 h-5" />,
  restaurant: <UtensilsCrossed className="w-5 h-5" />,
  other: <Sparkles className="w-5 h-5" />,
};

const CATEGORY_LABELS: Record<string, string> = {
  cafe: 'Café', hotel_lobby: 'Hotel Lobby', library: 'Library',
  coworking: 'Coworking', gym: 'Gym', restaurant: 'Restaurant', other: 'Other',
};


const ACCESS_LABELS: Record<string, string> = {
  free: 'Free to enter', purchase_required: 'Purchase required', members_only: 'Members only',
};

const categories = [
  { value: 'all', label: 'All', emoji: '📍' },
  { value: 'cafe', label: 'Cafés', emoji: '☕' },
  { value: 'hotel_lobby', label: 'Hotels', emoji: '🏨' },
  { value: 'library', label: 'Libraries', emoji: '📚' },
  { value: 'coworking', label: 'Coworking', emoji: '💻' },
  { value: 'gym', label: 'Gyms', emoji: '🏋️' },
  { value: 'restaurant', label: 'Restaurants', emoji: '🍽️' },
  { value: 'other', label: 'Other', emoji: '✨' },
];

type Phase =
  | 'loading'
  | 'map-fade'
  | 'markers-pop'
  | 'topbar-fade'
  | 'select-cafe'
  | 'hold-cafe'
  | 'select-library'
  | 'hold-library'
  | 'zoom-out'
  | 'end-card';

const SpotsAd = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const markerEls = useRef<Map<string, HTMLDivElement>>(new Map());
  const timeouts = useRef<ReturnType<typeof setTimeout>[]>([]);

  const [allSpots, setAllSpots] = useState<MapSpot[]>([]);
  const [phase, setPhase] = useState<Phase>('loading');
  const [selectedSpot, setSelectedSpot] = useState<MapSpot | null>(null);
  const [mapReady, setMapReady] = useState(false);
  

  // Fetch spots
  useEffect(() => {
    const fetchAllSpots = async () => {
      try {
        let page = 1;
        let allFetched: MapSpot[] = [];
        let hasMore = true;
        while (hasMore) {
          const res = await fetch(`${API_BASE}/admin/spots?page=${page}&page_size=100`);
          if (!res.ok) throw new Error('Failed to fetch');
          const data = await res.json();
          allFetched = [...allFetched, ...(data.spots || [])];
          hasMore = allFetched.length < data.total;
          page++;
        }
        setAllSpots(allFetched.filter(s => s.is_active && s.latitude != null && s.longitude != null));
      } catch (e) {
        console.error('Failed to load spots:', e);
      }
    };
    fetchAllSpots();
  }, []);

  // Init map (hidden initially via opacity)
  useEffect(() => {
    if (!mapContainer.current || map.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-0.0935, 51.5164],
      zoom: 12,
      dragRotate: false,
      touchZoomRotate: false,
      pitchWithRotate: false,
      interactive: false,
    });

    map.current.on('load', () => {
      map.current?.resize();
      setMapReady(true);
    });

    return () => {
      markersRef.current.forEach(m => m.remove());
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Helper to find a spot by category with a photo
  const findSpotByCategory = useCallback((category: string): MapSpot | null => {
    return allSpots.find(s => s.category === category && s.photos?.length) || 
           allSpots.find(s => s.category === category) || null;
  }, [allSpots]);

  // Add markers with staggered animation
  const addMarkersStaggered = useCallback(() => {
    if (!map.current) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    markerEls.current.clear();

    allSpots.forEach((spot, index) => {
      const emoji = CATEGORY_EMOJIS[spot.category] || '📍';

      // IMPORTANT: keep transforms off the root marker element so Mapbox can position it correctly.
      const markerRoot = document.createElement('div');
      markerRoot.style.cssText = `
        width:44px;
        height:44px;
        display:flex;
        align-items:center;
        justify-content:center;
        cursor:pointer;
      `;

      const bubble = document.createElement('div');
      bubble.style.cssText = `
        display:flex; align-items:center; justify-content:center;
        width:38px; height:38px;
        background:white;
        border-radius:50%;
        box-shadow:0 2px 8px rgba(0,0,0,0.18);
        font-size:17px;
        border:2px solid rgba(0,0,0,0.06);
        transform: scale(0);
        transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
                    width 0.2s ease, height 0.2s ease, font-size 0.2s ease,
                    background 0.2s ease, border 0.2s ease;
      `;
      bubble.textContent = emoji;
      markerRoot.appendChild(bubble);

      const marker = new mapboxgl.Marker({ element: markerRoot, anchor: 'center' })
        .setLngLat([spot.longitude!, spot.latitude!])
        .addTo(map.current!);

      markersRef.current.push(marker);
      markerEls.current.set(spot.id, bubble);

      // Stagger the pop-in on the inner bubble only
      const t = setTimeout(() => {
        bubble.style.transform = 'scale(1)';
      }, index * 30);
      timeouts.current.push(t);
    });
  }, [allSpots]);

  // Highlight / unhighlight a marker
  const highlightMarker = useCallback((spotId: string, highlight: boolean) => {
    const bubble = markerEls.current.get(spotId);
    if (!bubble) return;

    if (highlight) {
      bubble.style.width = '44px';
      bubble.style.height = '44px';
      bubble.style.fontSize = '20px';
      bubble.style.background = 'hsl(158, 33%, 18%)';
      bubble.style.border = '3px solid white';
      bubble.style.transform = 'scale(1.15)';
    } else {
      bubble.style.width = '38px';
      bubble.style.height = '38px';
      bubble.style.fontSize = '17px';
      bubble.style.background = 'white';
      bubble.style.border = '2px solid rgba(0,0,0,0.06)';
      bubble.style.transform = 'scale(1)';
    }
  }, []);

  // Run the scripted timeline after spots load
  useEffect(() => {
    if (allSpots.length === 0 || !map.current || !mapReady) return;

    const cafeSpot = findSpotByCategory('cafe');
    const librarySpot = findSpotByCategory('library');

    const schedule = (fn: () => void, ms: number) => {
      const t = setTimeout(fn, ms);
      timeouts.current.push(t);
    };

    // t=0: fade in map
    setPhase('map-fade');

    // t=1500: pop in markers
    schedule(() => {
      setPhase('markers-pop');
      
      addMarkersStaggered();
    }, 1500);

    // t=2500: show top bar
    schedule(() => setPhase('topbar-fade'), 2500);

    // t=3500: select cafe
    schedule(() => {
      if (cafeSpot) {
        setPhase('select-cafe');
        setSelectedSpot(cafeSpot);
        highlightMarker(cafeSpot.id, true);
        map.current?.easeTo({ center: [cafeSpot.longitude!, cafeSpot.latitude!], zoom: 14, duration: 800 });
      }
    }, 3500);

    // t=5500: hold cafe (already showing)
    schedule(() => setPhase('hold-cafe'), 5500);

    // t=7000: close cafe, select library
    schedule(() => {
      if (cafeSpot) highlightMarker(cafeSpot.id, false);
      setSelectedSpot(null);
    }, 7000);

    schedule(() => {
      if (librarySpot) {
        setPhase('select-library');
        setSelectedSpot(librarySpot);
        highlightMarker(librarySpot.id, true);
        map.current?.easeTo({ center: [librarySpot.longitude!, librarySpot.latitude!], zoom: 14, duration: 800 });
      }
    }, 7500);

    // t=9500: hold library
    schedule(() => setPhase('hold-library'), 9500);

    // t=11000: close library, zoom out
    schedule(() => {
      if (librarySpot) highlightMarker(librarySpot.id, false);
      setSelectedSpot(null);
      setPhase('zoom-out');
      map.current?.easeTo({ center: [-0.0935, 51.5164], zoom: 12, duration: 1200 });
    }, 11000);

    // t=12500: end card
    schedule(() => setPhase('end-card'), 12500);

    return () => {
      timeouts.current.forEach(clearTimeout);
      timeouts.current = [];
    };
  }, [allSpots, mapReady, addMarkersStaggered, findSpotByCategory, highlightMarker]);

  const showTopBar = ['topbar-fade', 'select-cafe', 'hold-cafe', 'select-library', 'hold-library', 'zoom-out', 'end-card'].includes(phase);
  const showDetailCard = selectedSpot && ['select-cafe', 'hold-cafe', 'select-library', 'hold-library'].includes(phase);
  const showEndCard = phase === 'end-card';
  const mapVisible = phase !== 'loading';

  return (
    <div className="w-screen h-screen bg-background flex items-center justify-center">
      <div className="relative w-[500px] h-[500px] rounded-2xl overflow-hidden shadow-2xl border border-border">
        {/* Map */}
        <div
          ref={mapContainer}
          className="absolute inset-0 transition-opacity duration-[1500ms] ease-out"
          style={{ opacity: mapVisible ? 1 : 0 }}
        />

        {/* Top bar overlay */}
        <AnimatePresence>
          {showTopBar && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="absolute top-0 left-0 right-0 z-20 px-4 pt-3 pb-2"
            >
              {/* Title bar */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 bg-background/95 backdrop-blur-xl rounded-full shadow-lg px-5 py-3 flex items-center gap-3">
                  <span className="font-funnel font-semibold text-foreground text-sm tracking-tight">qwip spots</span>
                  <span className="text-muted-foreground text-xs">•</span>
                  <span className="text-muted-foreground text-xs">London</span>
                </div>
              </div>
              {/* Category pills */}
              <div className="flex gap-2 overflow-hidden pb-1">
                {categories.slice(0, 5).map((cat) => (
                  <div
                    key={cat.value}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap shadow-sm ${
                      cat.value === 'all'
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'bg-background/90 backdrop-blur-sm text-foreground'
                    }`}
                  >
                    <span>{cat.emoji}</span>
                    {cat.label}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Detail card (bottom sheet style) */}
        <AnimatePresence>
          {showDetailCard && selectedSpot && (
            <motion.div
              key={`detail-${selectedSpot.id}`}
              initial={{ y: 300, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 300, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="absolute bottom-0 left-0 right-0 z-30 bg-background rounded-t-3xl shadow-[0_-4px_30px_rgba(0,0,0,0.12)] px-4 pt-3 pb-5"
            >
              {/* Drag handle (decorative) */}
              <div className="flex justify-center pb-3">
                <div className="w-9 h-1 bg-muted-foreground/25 rounded-full" />
              </div>

              {/* Photo */}
              {selectedSpot.photos?.[0] && (
                <img
                  src={selectedSpot.photos[0]}
                  alt={selectedSpot.name}
                  className="w-full h-28 object-cover rounded-xl mb-3"
                />
              )}

              {/* Category icon + name */}
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                  {CATEGORY_ICONS[selectedSpot.category] || CATEGORY_ICONS.other}
                </div>
                <div>
                  <h2 className="font-funnel text-lg font-bold text-foreground leading-tight">{selectedSpot.name}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">{CATEGORY_LABELS[selectedSpot.category] || selectedSpot.category}</p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-medium">
                  {ACCESS_LABELS[selectedSpot.access_type] || selectedSpot.access_type}
                </span>
                {(() => {
                  const noise = getCurrentNoise(selectedSpot.noise_matrix);
                  return noise ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-medium">
                      <Volume2 className="w-3 h-3" /> {noise.label}
                    </span>
                  ) : null;
                })()}
              </div>

              {/* Amenities row */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 p-2 rounded-xl bg-muted/50">
                  <div className="text-primary">{selectedSpot.wifi_available ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}</div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">WiFi</p>
                    <p className="text-xs font-medium text-foreground">{selectedSpot.wifi_available ? 'Available' : 'No'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-xl bg-muted/50">
                  <div className="text-primary"><Plug className="w-4 h-4" /></div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Power</p>
                    <p className="text-xs font-medium text-foreground">{selectedSpot.power_outlets ? 'Available' : 'No'}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* End card / branding overlay */}
        <AnimatePresence>
          {showEndCard && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="absolute inset-0 z-40 bg-foreground/80 backdrop-blur-md flex flex-col items-center justify-center gap-4"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5, type: 'spring' }}
                className="text-center"
              >
                <h1 className="font-funnel text-4xl font-bold text-background tracking-tight">qwip spots</h1>
                <p className="text-background/70 text-base mt-2 font-medium leading-snug max-w-[280px] mx-auto">A map of the best spaces across London to work or study from</p>
                <div className="mt-6 px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                  qwip.co.uk/spots
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading state */}
        {phase === 'loading' && (
          <div className="absolute inset-0 z-50 bg-background flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

export default SpotsAd;
